package com.iregained.site.components.sections.about.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.about.styles.AboutValuesContainerStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.region.Region
import com.iregained.site.region.RegionConfig
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Color.Companion.argb
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLInputElement
import org.w3c.fetch.NO_CORS
import org.w3c.fetch.RequestInit
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.js.json

// RESOLVES: Unresolved reference 'encodeURIComponent'
external fun encodeURIComponent(str: String): String

// UNIQUE ANIMATION: Banner Sweep & Squeeze
@Composable
private fun Modifier.bannerSweep(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 50.px).transition(
        Transition.of("opacity", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
private fun Modifier.gentleSqueeze(isVisible: Boolean): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 1.02f).transition(
        Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut),
        Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut)
    )
}

val CareerBannerImageStyle = CssStyle {
    base {
        Modifier.transition(
            Transition.of("transform", 0.3.s, TransitionTimingFunction.EaseOut),
            Transition.of("box-shadow", 0.3.s, TransitionTimingFunction.EaseOut)
        ).boxShadow(0.px, 4.px, 15.px, color = argb(0.1f, 0f, 0f, 0f))
    }
    hover {
        Modifier.scale(1.02f).boxShadow(0.px, 12.px, 25.px, color = argb(0.15f, 0f, 0f, 0f))
    }
}

val CareerFormInputStyle = CssStyle.base {
    Modifier.fillMaxWidth().padding(16.px).borderRadius(8.px).border(1.px, LineStyle.Solid, Colors.LightGray)
        .backgroundColor(Color.rgb(0xF9FAFB)).fontSize(16.px)
        .transition(Transition.of("border-color", 0.3.s), Transition.of("box-shadow", 0.3.s))
}

val CareerSubmitButtonStyle = CssStyle.base {
    Modifier.fillMaxWidth().padding(topBottom = 16.px).backgroundColor(Color.rgb(0x008080)).color(Colors.White)
        .borderRadius(8.px).fontSize(18.px).fontWeight(FontWeight.Bold).border(0.px)
}

@Composable
fun AboutCareersSection(data: AboutCareersSectionData = defaultAboutCareersData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "about-careers-section"
    var isVisible by remember { mutableStateOf(false) }

    // Form State for India Region
    var formSubmitted by remember { mutableStateOf(false) }
    var isSubmitting by remember { mutableStateOf(false) }

    Column(
        modifier = AboutValuesContainerStyle.toModifier().id(targetId).backgroundColor(palette.brand.primarySoft)
            .fillMaxWidth().padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
            .styleModifier { property("scroll-margin-top", "100px") },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Div(
            attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center).toAttrs {
                ref { element ->
                    val observer = IntersectionObserver { entries, obs ->
                        entries.forEach {
                            if (it.isIntersecting) {
                                isVisible = true; obs.unobserve(element)
                            }
                        }
                    }
                    observer.observe(element); onDispose { observer.disconnect() }
                }
            }) {
            Column(
                modifier = Modifier.fillMaxWidth().maxWidth(1200.px), horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (data.sectionTitle.isNotBlank()) {
                    SectionHeader(
                        text = data.sectionTitle,
                        fontSize = 32.px,
                        lineWidthPercent = 25.percent,
                        modifier = Modifier.margin(bottom = 16.px).opacity(if (isVisible) 1 else 0)
                            .transition(Transition.of("opacity", 0.8.s)),
                        alignment = Alignment.CenterHorizontally
                    )
                }

                if (data.sectionSubtitle.isNotBlank()) {
                    Div(
                        attrs = SubheadlineTextStyle.toModifier().maxWidth(900.px).fontSize(18.px)
                            .fontWeight(FontWeight.Medium).color(palette.custom.darkText).textAlign(TextAlign.Center)
                            .margin(bottom = 32.px).opacity(if (isVisible) 1 else 0)
                            .transition(Transition.of("opacity", 0.8.s, delay = 150.ms)).toAttrs {
                                ref { htmlElement ->
                                    htmlElement.innerHTML = data.sectionSubtitle; onDispose {}
                                }
                            })
                }

                // ==========================================
                // REGIONAL SPLIT LOGIC
                // ==========================================
                if (RegionConfig.currentRegion == Region.INDIA) {
                    // 🇮🇳 INDIA: Render the Application Form
                    Box(
                        modifier = Modifier.fillMaxWidth().maxWidth(800.px).backgroundColor(Colors.White)
                            .borderRadius(16.px).padding(32.px)
                            .boxShadow(0.px, 10.px, 30.px, color = argb(0.05f, 0f, 0f, 0f)).gentleSqueeze(isVisible)
                    ) {
                        Form(attrs = Modifier.fillMaxWidth().toAttrs {
                            attr("action", data.formProcessingEndpoint); attr("method", "POST")
                            onSubmit { e ->
                                e.preventDefault()
                                isSubmitting = true

                                val fullName =
                                    (document.getElementById("career_full_name") as? HTMLInputElement)?.value ?: ""
                                val email = (document.getElementById("career_email") as? HTMLInputElement)?.value ?: ""
                                val contact =
                                    (document.getElementById("career_contact") as? HTMLInputElement)?.value ?: ""
                                val location =
                                    (document.getElementById("career_location") as? HTMLInputElement)?.value ?: ""
                                val fileInput = document.getElementById("career_resume") as? HTMLInputElement

                                // RESOLVES: item(0) correctly accesses DOM FileList, typing as File resolves W3C mismatches
                                val file: File? = fileInput?.files?.item(0)

                                // Helper function to execute fetch once Base64 is ready
                                val submitToGoogleScript = { fileBase64: String, fileName: String, mimeType: String ->
                                    val formData =
                                        "form_id=career_application" + "&full_name=${encodeURIComponent(fullName)}" + "&email=${
                                            encodeURIComponent(email)
                                        }" + "&contact=${encodeURIComponent(contact)}" + "&location=${
                                            encodeURIComponent(
                                                location
                                            )
                                        }" + "&resume_base64=${encodeURIComponent(fileBase64)}" + "&resume_name=${
                                            encodeURIComponent(
                                                fileName
                                            )
                                        }" + "&resume_mime=${encodeURIComponent(mimeType)}"

                                    val requestInit = RequestInit(
                                        method = "POST",
                                        mode = org.w3c.fetch.RequestMode.NO_CORS,
                                        headers = json("Content-Type" to "application/x-www-form-urlencoded"),
                                        body = formData
                                    )
                                    window.fetch(data.formProcessingEndpoint, requestInit).then {
                                        isSubmitting = false; formSubmitted = true
                                        // Reset fields
                                        (document.getElementById("career_full_name") as? HTMLInputElement)?.value = ""
                                        (document.getElementById("career_email") as? HTMLInputElement)?.value = ""
                                        (document.getElementById("career_contact") as? HTMLInputElement)?.value = ""
                                        (document.getElementById("career_location") as? HTMLInputElement)?.value = ""
                                        (document.getElementById("career_resume") as? HTMLInputElement)?.value = ""
                                    }.catch { isSubmitting = false; window.alert(data.alertErrorMessage) }
                                }

                                // Read File as Base64 before submitting
                                if (file != null) {
                                    val reader = FileReader()
                                    reader.onload = { event ->
                                        val dataUrl = event.target.asDynamic().result.toString()
                                        val base64String = dataUrl.substringAfter(",") // Strip the data URL prefix
                                        submitToGoogleScript(base64String, file.name, file.type)
                                    }
                                    reader.readAsDataURL(file)
                                } else {
                                    submitToGoogleScript("", "", "")
                                }
                            }
                        }) {
                            Column(modifier = Modifier.gap(24.px)) {
                                Row(modifier = Modifier.fillMaxWidth().gap(24.px).flexWrap(FlexWrap.Wrap)) {
                                    Column(modifier = Modifier.flex(1).minWidth(250.px)) {
                                        Label(
                                            attrs = Modifier.margin(bottom = 8.px).fontWeight(FontWeight.Bold).toAttrs()
                                        ) { Text(data.fullNameLabel) }
                                        Input(
                                            type = InputType.Text, attrs = CareerFormInputStyle.toModifier().toAttrs {
                                                placeholder("John Doe"); attr(
                                                "id", "career_full_name"
                                            ); attr("required", "true")
                                            })
                                    }
                                    Column(modifier = Modifier.flex(1).minWidth(250.px)) {
                                        Label(
                                            attrs = Modifier.margin(bottom = 8.px).fontWeight(FontWeight.Bold).toAttrs()
                                        ) { Text(data.emailLabel) }
                                        Input(
                                            type = InputType.Email, attrs = CareerFormInputStyle.toModifier().toAttrs {
                                                placeholder("john@example.com"); attr(
                                                "id", "career_email"
                                            ); attr("required", "true")
                                            })
                                    }
                                }

                                Row(modifier = Modifier.fillMaxWidth().gap(24.px).flexWrap(FlexWrap.Wrap)) {
                                    Column(modifier = Modifier.flex(1).minWidth(250.px)) {
                                        Label(
                                            attrs = Modifier.margin(bottom = 8.px).fontWeight(FontWeight.Bold).toAttrs()
                                        ) { Text(data.contactLabel) }
                                        Input(type = InputType.Tel, attrs = CareerFormInputStyle.toModifier().toAttrs {
                                            placeholder("+91 98765 43210"); attr(
                                            "id", "career_contact"
                                        ); attr("required", "true")
                                        })
                                    }
                                    Column(modifier = Modifier.flex(1).minWidth(250.px)) {
                                        Label(
                                            attrs = Modifier.margin(bottom = 8.px).fontWeight(FontWeight.Bold).toAttrs()
                                        ) { Text(data.locationLabel) }
                                        Input(
                                            type = InputType.Text, attrs = CareerFormInputStyle.toModifier().toAttrs {
                                                placeholder("City, State"); attr(
                                                "id", "career_location"
                                            ); attr("required", "true")
                                            })
                                    }
                                }

                                Column(modifier = Modifier.fillMaxWidth()) {
                                    Label(
                                        attrs = Modifier.margin(bottom = 8.px).fontWeight(FontWeight.Bold).toAttrs()
                                    ) { Text(data.resumeLabel) }
                                    Input(
                                        type = InputType.File,
                                        attrs = CareerFormInputStyle.toModifier().padding(topBottom = 12.px).toAttrs {
                                            attr("id", "career_resume"); attr(
                                            "accept", ".pdf,.doc,.docx"
                                        ); attr("required", "true")
                                        })
                                    SpanText(
                                        text = "Accepted formats: PDF, DOCX (Max 5MB)",
                                        modifier = Modifier.fontSize(12.px).color(Colors.Gray).margin(top = 4.px)
                                    )
                                }

                                if (data.buttonText.isNotBlank()) {
                                    Button(
                                        attrs = CareerSubmitButtonStyle.toModifier()
                                            .opacity(if (isSubmitting) 0.6f else 1f)
                                            .cursor(if (isSubmitting) Cursor.NotAllowed else Cursor.Pointer).toAttrs {
                                                attr("type", "submit"); if (isSubmitting) attr("disabled", "true")
                                            }) { Text(if (isSubmitting) data.buttonSubmittingText else data.buttonText) }
                                }
                            }
                        }

                        // Modal for Submission Success
                        if (formSubmitted) {
                            Box(
                                modifier = Modifier.position(Position.Fixed).top(0.px).left(0.px).fillMaxSize()
                                    .zIndex(1000).backgroundColor(argb(0.6f, 0f, 0f, 0f))
                                    .onClick { formSubmitted = false }, contentAlignment = Alignment.Center
                            ) {
                                Column(
                                    modifier = Modifier.width(90.percent).maxWidth(500.px).backgroundColor(Colors.White)
                                        .borderRadius(8.px).padding(32.px)
                                        .boxShadow(0.px, 10.px, 30.px, color = argb(0.3f, 0f, 0f, 0f))
                                        .onClick { it.stopPropagation() },
                                    horizontalAlignment = Alignment.CenterHorizontally
                                ) {
                                    SpanText(
                                        text = "Application Submitted!",
                                        modifier = Modifier.fontSize(24.px).fontWeight(FontWeight.Bold)
                                            .margin(bottom = 8.px)
                                    )
                                    SpanText(
                                        text = "Thank you for your interest. Our team will review your profile and reach out soon.",
                                        modifier = Modifier.textAlign(TextAlign.Center).color(Colors.Gray)
                                            .margin(bottom = 24.px)
                                    )
                                    Button(
                                        attrs = CareerSubmitButtonStyle.toModifier()
                                            .toAttrs { onClick { formSubmitted = false } }) { Text("Close") }
                                }
                            }
                        }
                    }

                } else {
                    // 🇨🇦🇺🇸 US & CANADA (Default): Render the Indeed Banner Image
                    if (data.imagePath.isNotBlank() && data.externalLinkPath.isNotBlank()) {
                        A(
                            href = data.externalLinkPath,
                            attrs = Modifier.bannerSweep(isVisible, 300).toAttrs { attr("target", "_blank") }) {
                            Image(
                                src = data.imagePath,
                                modifier = CareerBannerImageStyle.toModifier().fillMaxWidth().height(100.px)
                                    .objectFit(ObjectFit.Cover).borderRadius(16.px).cursor(Cursor.Pointer)
                            )
                        }
                    }
                }

                if (data.generalInquiryText.isNotBlank()) {
                    Div(
                        attrs = Modifier.margin(top = 20.px).fontSize(16.px).color(palette.custom.darkText)
                            .textAlign(TextAlign.Center).whiteSpace(WhiteSpace.PreLine).opacity(if (isVisible) 1 else 0)
                            .transition(Transition.of("opacity", 0.8.s, delay = 500.ms)).toAttrs {
                                ref { htmlElement ->
                                    htmlElement.innerHTML = data.generalInquiryText; onDispose {}
                                }
                            })
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL: CAREERS
// ==========================================
data class AboutCareersSectionData(
    val sectionId: String? = null,
    val sectionTitle: String = "Join Our Interdisciplinary Team",
    val sectionSubtitle: String = "We are always looking for passionate problem solvers across mechatronic design, embedded systems, software development, and clinical research, who can join us in transforming future of rehabilitation.",

    // Default/NA Fallback Data
    val imagePath: String = Res.Images.About.Careers.INDEED,
    val externalLinkPath: String = "https://ca.indeed.com/cmp/Iregained-Inc.",
    val generalInquiryText: String = "Don't see a matching role? Send your Resume to <a href=\"mailto:info@iregained.rehab\" style=\"color: #008080; text-decoration: underline;\">careers@iregained.rehab</a>. \nWe will retain your profile on file and proactively reach out should a future opportunity align with your expertise.",

    // India Form Data
    val fullNameLabel: String = "Full Name",
    val emailLabel: String = "Email Address",
    val contactLabel: String = "Contact Number",
    val locationLabel: String = "City / State",
    val resumeLabel: String = "Upload Resume",
    val buttonText: String = "Submit Application",
    val buttonSubmittingText: String = "Uploading...",
    val alertErrorMessage: String = "Submission failed. Please try again.",

    val formProcessingEndpoint: String = "https://script.google.com/a/macros/iregained.rehab/s/AKfycbxGjRZicaotnihoNv7zquaM-Pt2mzYPURWqIT-eeCsqwUow8Hvlw-rq1ih7FNswyrx3/exec"
)

val defaultAboutCareersData = AboutCareersSectionData()